import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";

// ═══════════════════════════════════════════════════════════════════════════
// DATI DEL FORNITORE DELLA PIATTAFORMA — CONFIGURAZIONE UNICA
// ═══════════════════════════════════════════════════════════════════════════
// Chi gestisce Nexiora (ragione sociale, P.IVA, sede, PEC, contatti, logo) NON è scritto
// nel codice: sta in un solo documento Firestore, piattaforma/fornitore, che il Platform
// Owner compila dal suo pannello ("Dati del fornitore"). Lo leggono:
//   • l'app (questo file: link legali, contatti commerciali, intestazioni);
//   • le Cloud Functions (functions/index.js → leggiFornitore: piè di pagina delle email);
//   • il sito vetrina (fornitore.js del sito: pagine legali e piè di pagina).
// Finché i campi sono vuoti tutto funziona lo stesso: i campi mancanti semplicemente
// non compaiono (app, email) o compaiono come segnaposto tra parentesi (pagine legali).
// NON è un dato per organizzazione cliente: i dati delle aziende clienti restano nelle
// loro Impostazioni Organizzazione e sono quelli usati nei loro PDF.
// ═══════════════════════════════════════════════════════════════════════════

export const PERCORSO_FORNITORE = ["piattaforma", "fornitore"];

export const CAMPI_FORNITORE = [
  { key: "ragioneSociale", label: "Ragione sociale",            legale: true },
  { key: "piva",           label: "Partita IVA",                legale: true },
  { key: "codiceFiscale",  label: "Codice fiscale",             legale: true },
  { key: "sedeLegale",     label: "Sede legale",                legale: true },
  { key: "pec",            label: "PEC",                        legale: true },
  { key: "email",          label: "Email commerciale / privacy" },
  { key: "emailSupporto",  label: "Email assistenza" },
  { key: "telefono",       label: "Telefono" },
  { key: "logoUrl",        label: "Logo (indirizzo https dell'immagine)" },
  { key: "sitoWeb",        label: "Sito vetrina" },
  { key: "appUrl",         label: "Indirizzo dell'applicazione" },
];

export const FORNITORE_PREDEFINITO = {
  ragioneSociale: "", piva: "", codiceFiscale: "", sedeLegale: "", pec: "",
  email: "info@nexiora.it", emailSupporto: "support@nexiora.it", telefono: "", logoUrl: "",
  sitoWeb: "https://nexiora.it", appUrl: "https://app.nexiora.it",
};

export function normalizzaFornitore(dati) {
  const out = { ...FORNITORE_PREDEFINITO };
  CAMPI_FORNITORE.forEach(({ key }) => {
    const v = typeof dati?.[key] === "string" ? dati[key].trim() : "";
    if (v) out[key] = v;
  });
  out.sitoWeb = out.sitoWeb.replace(/\/+$/, "");
  out.appUrl = out.appUrl.replace(/\/+$/, "");
  return out;
}

/** Campi legali ancora da definire (utile per il pannello del Platform Owner). */
export const campiLegaliMancanti = (f) =>
  CAMPI_FORNITORE.filter(c => c.legale && !f?.[c.key]).map(c => c.label);

/** Riga identificativa, es. "ACME Srl · P.IVA 0123… · Via …". Vuota se manca la ragione sociale. */
export function rigaFornitore(f) {
  if (!f?.ragioneSociale) return "";
  return [f.ragioneSociale, f.piva && `P.IVA ${f.piva}`, f.sedeLegale].filter(Boolean).join(" · ");
}

export const urlLegali = (f) => ({
  condizioni: `${f.sitoWeb}/condizioni.html`,
  privacy: `${f.sitoWeb}/privacy.html`,
  cookie: `${f.sitoWeb}/cookie.html`,
  prezzi: `${f.sitoWeb}/prezzi.html`,
});

// Lettura condivisa: un solo ascolto per tutta l'app, anche se più componenti lo usano.
let ultimo = FORNITORE_PREDEFINITO;
const iscritti = new Set();
let stop = null;
function avvia() {
  if (stop) return;
  try {
    stop = onSnapshot(doc(db, ...PERCORSO_FORNITORE),
      snap => { ultimo = normalizzaFornitore(snap.exists() ? snap.data() : null); iscritti.forEach(fn => fn(ultimo)); },
      () => { /* documento assente o non leggibile: restano i valori predefiniti, l'app funziona */ });
  } catch { /* idem */ }
}

/** Valore corrente senza hook (per documenti e PDF). Avvia la lettura se non è già attiva. */
export function fornitoreCorrente() { avvia(); return ultimo; }
/** Dominio del sito da mostrare nei piè di pagina dei documenti, es. "nexiora.it". */
export const dominioSito = () => fornitoreCorrente().sitoWeb.replace(/^https?:\/\//, "");

/** Dati del fornitore sempre valorizzati (predefiniti finché il documento non c'è). */
export function useFornitore() {
  const [f, setF] = useState(ultimo);
  useEffect(() => { avvia(); iscritti.add(setF); setF(ultimo); return () => { iscritti.delete(setF); }; }, []);
  return f;
}
